import get from 'lodash/get';
import { defineAbility } from '@casl/ability';

import { WranglerRoleNames, wranglerRoleNames } from './lib/utils';

export const READ = 'read';
export const EDIT = 'edit';
export const CREATE = 'create';
export const DELETE = 'delete';

export const MARKET_PAGE = 'MarketPage';
export const MY_MARKETS = 'MyMarkets';
export const MARKET_VEHICLE_SUMMARY = 'MarketVehicleSummary';
export const REPAIR_TICKETS_SUMMARY = 'RepairTicketsSummary';
export const ALERTS_SUMMARY = 'AlertsSummary';
export const FIELD_REPORT_TABLE = 'FieldReportTable';
export const LOCATION_TABLE = 'LocationTable';
export const ZONES_TABLE = 'ZonesTable';
export const VEHICLE_ZONES_PAGE = 'VehicleZonesPage';
export const VEHICLE_TRIPS_TABLE = 'VehicleTripsTable';
export const ROUTES = 'Routes';
export const MAP_MODAL = 'MapModal';
export const USER_TABLE = 'UserTable';
export const MARKET_SETTINGS = 'MarketSettings';
export const DASHBOARD = 'Dashboard';
export const REPORTS_PAGE = 'ReportsPage';
export const USER_ADMIN = 'UserAdmin';
export const RUN_MANAGER = 'RunManager';

export const ZONE_DEPLOYMENT_TABLE = 'ZoneDeploymentTable';
export const DEPLOYMENT_PROGRESS_PAGE = 'DeploymentProgressPage';
export const REPAIR_TICKET_REPORT = 'RepairTicketPage';
export const REPAIRS_BY_CATEGORY_REPORT = 'RepairsByCategoryPage';
export const INVENTORY_REPORT = 'InventoryReport';
export const RIDE_REPORT = 'RideReport';

export const APP_CENTER_BUTTONS = 'AppCenterButtons';

function viewAllMarketPages(allow) {
  const marketPages = [
    MARKET_PAGE,

    MY_MARKETS,
    MARKET_VEHICLE_SUMMARY,
    REPAIR_TICKETS_SUMMARY,
    ALERTS_SUMMARY,
    FIELD_REPORT_TABLE,
    LOCATION_TABLE,
    ZONES_TABLE,
    VEHICLE_ZONES_PAGE,
    ROUTES,
    RUN_MANAGER,
    MAP_MODAL,
    USER_TABLE,
    MARKET_SETTINGS,
    VEHICLE_TRIPS_TABLE,
    DASHBOARD,
  ];
  marketPages.forEach((page) => allow(READ, page));
}

function viewMarketReports(allow) {
  allow(READ, REPORTS_PAGE);

  allow(READ, ZONE_DEPLOYMENT_TABLE);
  allow(READ, DEPLOYMENT_PROGRESS_PAGE);
  allow(READ, REPAIR_TICKET_REPORT); // market + brand, restricted
  allow(READ, REPAIRS_BY_CATEGORY_REPORT); // market + brand, restricted
  allow(READ, INVENTORY_REPORT); // market + brand, restricted
  allow(READ, RIDE_REPORT); // market + brand, restricted
}

function viewBrandReports(allow) {
  allow(READ, REPORTS_PAGE);

  // allow(READ, ZONE_DEPLOYMENT_TABLE); // market-only
  allow(READ, DEPLOYMENT_PROGRESS_PAGE);
  allow(READ, REPAIR_TICKET_REPORT); // market + brand, restricted
  allow(READ, REPAIRS_BY_CATEGORY_REPORT); // market + brand, restricted
  allow(READ, INVENTORY_REPORT); // market + brand, restricted
  allow(READ, RIDE_REPORT); // market + brand, restricted
}

function viewFeatures(allow) {
  allow(READ, APP_CENTER_BUTTONS);
}

// second 'forbid' argument to each fn is unused; these settings are whitelisted, not blacklisted
export const Admin = defineAbility((allow) => {
  allow(READ, 'all');
  allow(EDIT, 'all');
});
export const CorporateOM = defineAbility((allow) => {
  viewAllMarketPages(allow);
  viewMarketReports(allow);
  viewBrandReports(allow);
  viewFeatures(allow);
  allow(READ, USER_ADMIN);
  allow(EDIT, 'all');
});
export const MarketOM = defineAbility((allow) => {
  const pages = [
    // MARKET_PAGE,
    MY_MARKETS,
    MARKET_VEHICLE_SUMMARY,
    REPAIR_TICKETS_SUMMARY,
    ALERTS_SUMMARY,
    FIELD_REPORT_TABLE,
    LOCATION_TABLE,
    ZONES_TABLE,
    VEHICLE_ZONES_PAGE,
    ROUTES,
    RUN_MANAGER,
    MAP_MODAL,
    USER_TABLE,
    MARKET_SETTINGS,
    VEHICLE_TRIPS_TABLE,
    USER_ADMIN,
    DASHBOARD,
  ];
  pages.forEach((page) => allow(READ, page));
  viewMarketReports(allow);
  viewFeatures(allow);
  allow(EDIT, 'all');
});
export const BrandPartner = defineAbility((allow) => {
  viewBrandReports(allow);
});
export const OpsAssociate = defineAbility(() => {
  // role needs to exist, admin access may not be allowed
});
export const PropertyManager = defineAbility(() => {
  // role needs to exist, admin access may not be allowed
});
export const Mechanic = defineAbility((allow) => {
  allow(READ, MARKET_PAGE);
  allow(READ, MARKET_VEHICLE_SUMMARY);
  allow(READ, REPAIR_TICKETS_SUMMARY);
  allow(READ, MAP_MODAL);
  allow(READ, FIELD_REPORT_TABLE);
});
export const TplManager = defineAbility((allow) => {
  const pages = [
    MY_MARKETS,
    MARKET_VEHICLE_SUMMARY,
    REPAIR_TICKETS_SUMMARY,
    ALERTS_SUMMARY,
    FIELD_REPORT_TABLE,
    LOCATION_TABLE,
    ZONES_TABLE,
    VEHICLE_ZONES_PAGE,
    ROUTES,
    RUN_MANAGER,
    MAP_MODAL,
    MARKET_SETTINGS,
    VEHICLE_TRIPS_TABLE,
    DASHBOARD,
  ];
  pages.forEach((page) => allow(READ, page));
  viewMarketReports(allow);
  allow(EDIT, 'all');
});
export const Base = Admin;

const accessLevels = {
  [WranglerRoleNames.ADMIN]: Admin,
  [WranglerRoleNames.CORPORATE_OM]: CorporateOM,
  [WranglerRoleNames.MARKET_OM]: MarketOM,
  [WranglerRoleNames.BRAND_PARTNER]: BrandPartner,
  [WranglerRoleNames.OPERATIONS_ASSOCIATE]: OpsAssociate,
  [WranglerRoleNames.PROPERTY_MANAGER]: PropertyManager,
  [WranglerRoleNames.MECHANIC]: Mechanic,
  [WranglerRoleNames.TPL_MANAGER]: TplManager,
};

function getAccess(wrangler) {
  return get(accessLevels, wrangler.role, Base);
}

export default getAccess;

export const ACTIONS = {
  READ, EDIT, CREATE, DELETE,
};

export const PAGES = {
  MARKET_PAGE,
  MY_MARKETS,
  MARKET_VEHICLE_SUMMARY,
  REPAIR_TICKETS_SUMMARY,
  ALERTS_SUMMARY,
  FIELD_REPORT_TABLE,
  LOCATION_TABLE,
  ZONES_TABLE,
  VEHICLE_ZONES_PAGE,
  ROUTES,
  MAP_MODAL,
  USER_TABLE,
  MARKET_SETTINGS,
  DASHBOARD,
  REPORTS_PAGE,
  USER_ADMIN,
  ZONE_DEPLOYMENT_TABLE,
  DEPLOYMENT_PROGRESS_PAGE,
  REPAIR_TICKET_REPORT,
  REPAIRS_BY_CATEGORY_REPORT,
  RUN_MANAGER,
};

export const FEATURES = {
  APP_CENTER_BUTTONS,
};

const marketManagerRoles = [
  WranglerRoleNames.OPERATIONS_ASSOCIATE,
  WranglerRoleNames.PROPERTY_MANAGER,
  WranglerRoleNames.MECHANIC,
  WranglerRoleNames.MARKET_OM,
  WranglerRoleNames.TPL_MANAGER,
];
const corporateManagerRoles = [
  WranglerRoleNames.CORPORATE_OM,
  WranglerRoleNames.MARKET_OM,
  WranglerRoleNames.BRAND_PARTNER,
  WranglerRoleNames.OPERATIONS_ASSOCIATE,
  WranglerRoleNames.PROPERTY_MANAGER,
  WranglerRoleNames.MECHANIC,
  WranglerRoleNames.TPL_MANAGER,
];
const adminRoles = wranglerRoleNames;

export function getWranglerRoleNamesForUser(user) {
  const userRole = get(user, 'role');
  switch (userRole) {
    case WranglerRoleNames.ADMIN:
      return adminRoles;
    case WranglerRoleNames.CORPORATE_OM:
      return corporateManagerRoles;
    case WranglerRoleNames.MARKET_OM:
      return marketManagerRoles;
    default:
      return adminRoles;
  }
}
